import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import heartLoading from "../../../assests/heartLoading.gif";
import axios from "axios";
import { UpstreamNetworkView } from "./UpstreamNetworkView";
import { ConsumerNetworkView } from "./ConsumerNetworkView";
import UpwardTreeView from "./UpwardTreeView";
import DownwardTreeView from "./DownwardTreeView";
import { ChevronLeft } from "lucide-react";
import { DependencyViewControls } from "./DependencyViewControls";

export default function DataContractFullScreenPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const contractId = searchParams.get("contractId") || "";
  const viewMode = searchParams.get("viewMode") || "network";

  const [contract, setContract] = useState(null);
  const [consumerData, setConsumerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("downstream"); // "upstream" or "downstream" (default: downstream)
  const [viewType, setViewType] = useState("network"); // "network" or "tree" (default: network)

  // Collect all unique product IDs from both upstream and downstream dependencies
  const graphProductIds = useMemo(() => {
    const ids = new Set();
    
    // Add root contract ID (support both id and product_id)
    if (contractId) {
      ids.add(contractId);
      // Try to add product_id if available
      if (contract?.product_id) ids.add(contract.product_id);
    }

    const collectProductIds = (node) => {
      if (!node) return;
      // Add both id and product_id to handle different API responses
      if (node.id) ids.add(node.id);
      if (node.product_id) ids.add(node.product_id);
      
      if (node.dependencies && Array.isArray(node.dependencies)) {
        node.dependencies.forEach(dep => collectProductIds(dep));
      }
    };

    // Add all upstream dependencies recursively
    if (consumerData?.upstreamDependencies) {
      consumerData.upstreamDependencies.forEach(dep => collectProductIds(dep));
    }

    // Add all downstream dependencies recursively
    if (consumerData?.consumerDependencies) {
      consumerData.consumerDependencies.forEach(dep => collectProductIds(dep));
    }

    return ids;
  }, [contractId, contract?.product_id, consumerData?.upstreamDependencies, consumerData?.consumerDependencies]);

  useEffect(() => {
    const fetchData = async () => {
      if (!contractId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Try to fetch contract details, but don't fail if it doesn't exist
        try {
          const contractResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/get-data-contract/${contractId}`
          );
          setContract(contractResponse.data);
        } catch (err) {
          // If contract fetch fails, create a minimal contract object with just the ID
          console.error("Contract fetch failed:", err);
        }

        // Fetch consumer dependencies
        const consumerResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-consumer-dependencies/${contractId}`
        );
        setConsumerData(consumerResponse.data);
      } catch (err) {
        console.error("Failed to fetch consumer data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contractId]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (contractId) {
      nextParams.set("contractId", contractId);
    } else {
      nextParams.delete("contractId");
    }

    nextParams.set("viewMode", viewMode);

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [contractId, viewMode, searchParams, setSearchParams]);

  return (
    <div className="h-screen bg-slate-100">
      <div className="flex h-full flex-col">
        {/* Header */}
        {contractId && contract && !loading && (
          <div className="border-b border-gray-200 bg-white px-6 py-2">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.history.back()}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Go back"
              >
                <ChevronLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {contract?.name || contractId}
                </h2>
                <p className="text-xs text-gray-500">Network Visualization</p>
              </div>

              {/* View Controls */}
              <DependencyViewControls
                viewMode={viewType}
                onViewModeChange={setViewType}
                renderType={currentView}
                onRenderTypeChange={setCurrentView}
                compact={false}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-hidden bg-white p-0">
          {contractId && contract && !loading && consumerData ? (
            viewType === "network" ? (
              currentView === "upstream" ? (
                <UpstreamNetworkView
                  consumers={consumerData?.upstreamDependencies || []}
                  contractName={contract?.name}
                  contractStatus={contract?.status}
                  contractProductId={contractId}
                  contractSubscribedAssets={consumerData?.subscribedAssets || []}
                  graphProductIds={graphProductIds}
                  isFullScreen={true}
                />
              ) : (
                <ConsumerNetworkView
                  consumers={consumerData?.consumerDependencies || []}
                  contractName={contract?.name}
                  contractStatus={contract?.status}
                  contractProductId={contractId}
                  contractSubscribedAssets={consumerData?.subscribedAssets || []}
                  graphProductIds={graphProductIds}
                  isFullScreen={true}
                />
              )
            ) : (
              currentView === "upstream" ? (
                <UpwardTreeView
                  contractName={contract?.name}
                  contractStatus={contract?.status}
                  contractProductId={contractId}
                  contractSubscribedAssets={consumerData?.subscribedAssets || []}
                  graphProductIds={graphProductIds}
                  isFullScreen={true}
                  dependencies={consumerData?.upstreamDependencies || []}
                />
              ) : (
                <DownwardTreeView
                  contractName={contract?.name}
                  contractStatus={contract?.status}
                  contractProductId={contractId}
                  contractSubscribedAssets={consumerData?.subscribedAssets || []}
                  graphProductIds={graphProductIds}
                  isFullScreen={true}
                  dependencies={consumerData?.consumerDependencies || []}
                />
              )
            )
          ) : loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="flex flex-col items-center space-y-3">
                <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
                <span className="text-gray-700 text-[20px] text-center">
                  Loading dependencies...
                </span>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center">
              <div className="space-y-2 px-6">
                <p className="text-lg font-semibold text-slate-800">
                  No contract selected
                </p>
                <p className="text-sm text-slate-500">
                  Open this page from the data traffic lights modal to load the full screen view.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
